import React from "react";
import { Radio as RadioIcon, ArrowLeft, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { TAPIN_RADIO_LOGO } from "../../lib/radioBrand";

/**
 * RadioHostToolbar — top header for the DJ studio.
 *
 * Pulled out of RadioHostStudio so the page file stays under ~700 lines
 * per the codebase guideline. Pure presentation: every action is a
 * callback prop, no internal data fetching. State (live, autoRecord,
 * smartMix) lives in the parent because multiple panels depend on it.
 */
export default function RadioHostToolbar({
  station,
  navigate,
  live,
  goLive,
  endShow,
  onOpenHistory,
  autoRecord,
  toggleAutoRecord,
  smartMix,
  setSmartMix,
}) {
  return (
    <header
      data-testid="radio-host-toolbar"
      className="flex items-center gap-3 px-4 py-2 bg-[#06090F] border-b border-white/10 shrink-0"
    >
      <button
        onClick={() => navigate("/radio/host")}
        title="Back"
        className="p-2 hover:bg-white/5 rounded shrink-0"
        data-testid="radio-host-back"
      >
        <ArrowLeft className="w-5 h-5" />
      </button>
      <img
        src={TAPIN_RADIO_LOGO}
        alt="Tap IN Radio"
        className="h-10 w-10 shrink-0 rounded bg-black object-contain"
        data-testid="tapin-radio-logo"
      />
      <div className="min-w-0 flex-1">
        <div className="font-display font-black text-sm leading-tight truncate">
          <span className="text-red-400">TAP IN</span> RADIO · DJ Studio
        </div>
        <div className="text-white/50 text-[10px] uppercase tracking-wider truncate">
          {station.name} · Keeping You Tapped In
        </div>
      </div>
      <div className="ml-auto flex items-center gap-2 shrink-0">
        {live ? (
          <>
            <span className="inline-flex items-center gap-1 bg-tv-live/20 text-red-200 border border-tv-live/40 px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-wider">
              <span className="w-2 h-2 bg-tv-live rounded-full animate-pulse" /> ON AIR
            </span>
            <button
              data-testid="radio-host-end"
              onClick={endShow}
              className="bg-tv-live hover:bg-red-600 text-white font-bold text-sm px-3 py-1.5 rounded"
            >
              End Show
            </button>
          </>
        ) : (
          <button
            data-testid="radio-host-golive"
            onClick={goLive}
            className="bg-tv-accent hover:bg-yellow-400 text-black font-bold text-sm px-4 py-1.5 rounded flex items-center gap-1"
          >
            <RadioIcon className="w-4 h-4" /> Go Live
          </button>
        )}
        {/* Post-show analytics — opens a modal listing the host's last
            shows with caller / request / tip metrics. Same modal opens
            automatically when the host taps End Show. */}
        <button
          data-testid="radio-host-history-btn"
          onClick={onOpenHistory}
          title="Past shows: callers, requests, tips"
          className="text-[10px] uppercase tracking-wider font-black border rounded px-2 py-1.5 transition bg-white/5 text-white/55 border-white/15 hover:text-white inline-flex items-center gap-1"
        >
          <BarChart3 className="w-3.5 h-3.5" /> History
        </button>
        {/* Auto-record toggle — when ON, the host's live broadcast is
            automatically saved as a replay (the live-stream creation
            path reads dj_auto_record on the user). */}
        <button
          data-testid="radio-host-auto-record-toggle"
          onClick={toggleAutoRecord}
          title={autoRecord ? "Auto-record ON — every show is saved as a replay. Click to disable." : "Auto-record OFF — your shows are live-only. Click to enable replays."}
          aria-pressed={autoRecord}
          className={`text-[10px] uppercase tracking-wider font-black border rounded px-2 py-1.5 transition ${
            autoRecord
              ? "bg-rose-500/30 text-rose-100 border-rose-400/60"
              : "bg-white/5 text-white/55 border-white/15 hover:text-white"
          }`}
        >
          {autoRecord ? "REC ●" : "Auto-rec"}
        </button>
        {/* Smart-mix beat-align toggle — adjusts auto-mix sweep duration
            to the nearest multiple of 8 beats so transitions land on
            the bar instead of mid-measure. */}
        <button
          data-testid="radio-host-smart-mix-toggle"
          onClick={() => setSmartMix((v) => !v)}
          title={smartMix ? "Smart-mix beat-align ON — sweep snaps to the bar." : "Smart-mix OFF — sweep uses default 2.4s duration."}
          aria-pressed={smartMix}
          className={`text-[10px] uppercase tracking-wider font-black border rounded px-2 py-1.5 transition ${
            smartMix
              ? "bg-cyan-500/30 text-cyan-100 border-cyan-400/60"
              : "bg-white/5 text-white/55 border-white/15 hover:text-white"
          }`}
        >
          {smartMix ? "Smart Mix ●" : "Smart Mix"}
        </button>
        <RoyaltyCsvLink />
      </div>
    </header>
  );
}

/**
 * Royalty CSV — quick download of every track loaded by this host,
 * ready for ASCAP/BMI/SOCAN filing. Server caps at 5000 rows.
 *
 * Fetches the file as a blob (so we can attach the auth header) and
 * triggers a hidden-anchor download. Hidden when REACT_APP_BACKEND_URL
 * isn't configured — failing fast is better than silently producing a
 * broken `/api/...` link relative to the current origin.
 */
function RoyaltyCsvLink() {
  const backend = process.env.REACT_APP_BACKEND_URL;
  if (!backend) return null;
  const onClick = async (e) => {
    e.preventDefault();
    try {
      const r = await fetch(`${backend}/api/dj/royalty-report?format=csv`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("tv_token") || ""}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `royalty-report-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(url);
      toast.success("Royalty CSV downloaded");
    } catch { toast.error("Couldn't download royalty CSV"); }
  };
  return (
    <a
      data-testid="radio-host-royalty-csv"
      href={`${backend}/api/dj/royalty-report?format=csv&_=${Date.now()}`}
      onClick={onClick}
      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-black text-white/55 hover:text-white border border-white/15 rounded px-2 py-1.5 transition"
      title="Download every track you've loaded as a CSV — drop into your ASCAP/BMI/SOCAN filing"
    >
      Royalty CSV
    </a>
  );
}
